import { useEffect, useRef, type FocusEvent, type KeyboardEvent, type MouseEvent as ReactMouseEvent, type RefObject } from "react";

// How something opened over the page goes away: Esc, a press outside it, and
// — for a menu or a popover, not a modal — the focus leaving it. The caller
// says what closing is (and where the focus goes): `how` tells it which, since
// Esc gives the focus back to what opened it and a press elsewhere does not.
// "opener" is the caller's own, for a second press on the button that opened
// it.
//
// That button's press must not count as the focus leaving: Safari and Firefox
// on macOS do not focus a button that is clicked, so pressing it blurs what
// had the focus inside with nothing to go to, which closes it, and the click
// that follows would open it again. `keepFocus`, the opener's onMouseDown,
// keeps the focus where it is while it is open.
//
// Nor must a press inside on something that takes no focus — the command in
// Resume's popover, a role's words in Invite's: every browser then blurs
// what had the focus with nothing to go to (no relatedTarget), and Safari
// does so for a button or a field too. The focus leaving means it went
// somewhere outside (Tab); a press is the mousedown's to judge. For the same
// reason Esc is heard on the document while it is open, not only inside: in
// Safari, after such a press, the focus is on the page's body.

export type Dismissed = "escape" | "outside" | "focus" | "opener";

export function useDismiss(
  open: boolean,
  dismiss: (how: Dismissed) => void,
  /** What counts as inside: the popup, and the button that opens it. */
  inside: RefObject<HTMLElement | null>[],
  { onFocusOut = false }: { onFocusOut?: boolean } = {},
): {
  onKeyDown: (e: KeyboardEvent) => void;
  onBlur?: (e: FocusEvent) => void;
  keepFocus: (e: MouseEvent | ReactMouseEvent) => void;
} {
  const latest = useRef(dismiss);
  latest.current = dismiss;
  const within = (n: EventTarget | null) => n instanceof Node && inside.some((r) => r.current?.contains(n));

  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => {
      if (!within(e.target)) latest.current("outside");
    };
    // after the popup's own onKeyDown, which takes Esc pressed inside
    const esc = (e: globalThis.KeyboardEvent) => {
      if (e.key === "Escape" && !e.defaultPrevented) {
        e.preventDefault();
        latest.current("escape");
      }
    };
    document.addEventListener("mousedown", away);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("mousedown", away);
      document.removeEventListener("keydown", esc);
    };
    // the refs are stable; `within` reads them when it runs
  }, [open]);

  return {
    onKeyDown: (e) => {
      if (open && e.key === "Escape") {
        e.preventDefault();
        latest.current("escape");
      }
    },
    onBlur: onFocusOut
      ? (e) => {
          // nowhere (a press inside on what takes no focus) is not leaving
          if (open && e.relatedTarget !== null && !within(e.relatedTarget)) latest.current("focus");
        }
      : undefined,
    keepFocus: (e) => {
      if (open) e.preventDefault();
    },
  };
}
